import { NfcLayout } from "Components/Layout/NfcLayout";
import { Resources, useResource } from "Translations/Resources";
import { Typography } from "@mui/material";
import { usePayPointOwnerQuery } from "Api/Queries/PayPoints/usePayPointOwnerQuery";
import { getApiUrl } from "Infrastructure/Api/Api";
import { CenteredCircularProgress } from "Components/Shared/CenteredCircularProgress";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { LoggedUserContent } from "Components/Paypoints/IdlePaypoint/LoggedUserContent";
import { AnonymousUserContent } from "Components/Paypoints/IdlePaypoint/AnonymousUserContent";
import { useEffect } from "react";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import { setLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";
import { getContextProfilePhotoUrl } from "Utils/Image/getProfilePhotoUrl";

const RESOURCES = Resources.Paypoint;

type Props = {
  publicID: string;
};

export const IdlePaypointInfo = ({ publicID }: Props) => {
  const { data, isLoading, isError, error } = usePayPointOwnerQuery(publicID);
  const user = useLoggedInUser();
  const { t } = useResource();
  const affiliateCode = data?.affiliateCode;

  useEffect(() => {
    if (affiliateCode) {
      setLocalStorageItem(LocalStorageKeys.AffiliateCode, affiliateCode);
    }
  }, [affiliateCode]);

  if (isLoading) {
    return <CenteredCircularProgress />;
  }

  if (isError || !data) {
    return (
      <NfcLayout title={t(RESOURCES.Paypoint)}>
        {!!error?.status && (
          <Typography variant="h6" textAlign="center">
            {error.status}
          </Typography>
        )}
        <Typography variant="h6">{t(RESOURCES.LoadingError)}</Typography>
      </NfcLayout>
    );
  }
  const {
    name,
    logoUrl,
    merchantIdentityUserPublicID,
    merchantProfilePicture,
  } = data;
  const ownerName = name || t(RESOURCES.UnknownOwner);
  // Facility logo first, otherwise the owning merchant's avatar (ZS-1607).
  const logoApiUrl = logoUrl
    ? `${getApiUrl()}${logoUrl}`
    : getContextProfilePhotoUrl(
        merchantIdentityUserPublicID,
        merchantProfilePicture,
      );

  if (user) {
    return (
      <NfcLayout title={t(RESOURCES.Paypoint)}>
        <LoggedUserContent owner={ownerName} logoUrl={logoApiUrl} />
      </NfcLayout>
    );
  }

  return (
    <NfcLayout title={t(RESOURCES.Paypoint)}>
      <AnonymousUserContent
        owner={ownerName}
        logoUrl={logoApiUrl}
        affiliateCode={affiliateCode || undefined}
      />
    </NfcLayout>
  );
};
